import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronsLeftRight, Expand, X } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const FILTERS = [
    { key: "all", label: "All" },
    { key: "drywall", label: "Drywall" },
    { key: "paint", label: "Paint" },
    { key: "tile", label: "Tile & Marble" },
    { key: "flooring", label: "Hardwood Flooring" },
];

const PROJECTS = [
    { id: 1, cat: "paint", img: "/work/kitchen-white.webp", before: "/work/kitchen-before.webp", title: "Bright White Kitchen", scope: "Cabinet refresh, quartz counters & under-cabinet lighting", stage: "Completed" },
    { id: 2, cat: "drywall", img: "/work/bath-after.webp", before: "/work/bath-drywall.webp", title: "Bathroom Rebuild", scope: "Drywall patching, shower fit-out & fixture install", stage: "Final touches" },
    { id: 3, cat: "tile", img: "/work/kitchen-backsplash.webp", before: null, title: "Backsplash & Granite Kitchen", scope: "Diamond-pattern tile backsplash with granite counters", stage: "Work in progress" },
    { id: 4, cat: "tile", img: "/work/sink-after.webp", before: "/work/counter-sink.webp", title: "Countertop & Sink Install", scope: "New solid-surface counter with sink & matte black faucet", stage: "Completed" },
    { id: 5, cat: "flooring", img: "/work/room-tilefloor.webp", before: "/work/room-drywall.webp", title: "Large-Format Tile Floor", scope: "Marble-look tile over fresh paint & new trim", stage: "Completed" },
    { id: 6, cat: "flooring", img: "/work/hardwood-gloss.webp", before: "/work/hardwood-oak.webp", title: "Oak Hardwood Refinish", scope: "Raw oak sanded, sealed & finished with a gloss topcoat", stage: "Completed" },
    { id: 7, cat: "paint", img: "/work/kitchen-spraying.webp", before: "/work/kitchen-masked.webp", title: "Cabinet Spray Repaint", scope: "Cream cabinets masked, sprayed & refinished in white", stage: "Work in progress" },
    { id: 8, cat: "drywall", img: "/work/tvwall-after.webp", before: "/work/tvwall-before.webp", title: "Entertainment Wall Build-Out", scope: "Framed drywall niche wall with LED-lit display cubbies", stage: "Completed" },
];

const BeforeAfterSlider = ({ before, after, alt, className = "" }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState(50);
    const dragging = useRef(false);

    const update = (clientX) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r || !r.width) return;
        setPos(Math.min(97, Math.max(3, ((clientX - r.left) / r.width) * 100)));
    };

    return (
        <div
            ref={ref}
            data-testid="portfolio-compare-slider"
            role="slider"
            tabIndex={0}
            aria-label={`Before and after comparison: ${alt}`}
            aria-valuenow={Math.round(pos)}
            aria-valuemin={0}
            aria-valuemax={100}
            onPointerDown={(e) => {
                e.stopPropagation();
                dragging.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                update(e.clientX);
            }}
            onPointerMove={(e) => dragging.current && update(e.clientX)}
            onPointerUp={() => (dragging.current = false)}
            onPointerCancel={() => (dragging.current = false)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                if (e.key === "ArrowLeft") setPos((p) => Math.max(3, p - 5));
                if (e.key === "ArrowRight") setPos((p) => Math.min(97, p + 5));
            }}
            className={`group/slider relative cursor-ew-resize select-none overflow-hidden bg-sand ${className}`}
            style={{ touchAction: "pan-y" }}
        >
            <img src={after} alt={alt} draggable={false} loading="lazy" className="block h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
                <img src={before} alt={`Before: ${alt}`} draggable={false} loading="lazy" className="block h-full w-full object-cover" />
            </div>

            <span className="pointer-events-none absolute left-3 top-3 bg-espresso/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-bone backdrop-blur-sm">
                Before
            </span>
            <span className="pointer-events-none absolute right-3 top-3 bg-oak/90 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-bone backdrop-blur-sm">
                After
            </span>

            <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
                <div className="absolute inset-y-0 -ml-px w-0.5 bg-bone/90" />
                <div className="absolute top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-bone bg-oak text-bone shadow-frame transition-transform duration-200 group-active/slider:scale-90">
                    <ChevronsLeftRight className="h-4 w-4" />
                </div>
            </div>

            <span className="pointer-events-none absolute bottom-3 right-3 bg-espresso/60 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-bone/90 backdrop-blur-sm">
                Drag to compare
            </span>
        </div>
    );
};

export const Portfolio = () => {
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);

    const visible = PROJECTS.filter((p) => filter === "all" || p.cat === filter);

    return (
        <section id="portfolio" className="scroll-mt-20 bg-sand/60 py-20 md:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <Eyebrow>Recent work</Eyebrow>
                        <h2 className="mt-5 max-w-xl font-serif text-2xl font-normal leading-[1.15] tracking-tight text-espresso sm:text-3xl md:text-4xl">
                            Finished spaces, from{" "}
                            <span className="italic text-oak">Boca to the Palm Beaches.</span>
                        </h2>
                    </div>

                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter portfolio">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                data-testid={`portfolio-filter-${f.key}`}
                                onClick={() => setFilter(f.key)}
                                className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                                    filter === f.key
                                        ? "border-espresso bg-espresso text-bone"
                                        : "border-line bg-transparent text-clay hover:border-espresso hover:text-espresso"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                        {visible.map((p) => (
                            <motion.div
                                layout
                                key={p.id}
                                data-testid={`portfolio-item-${p.id}`}
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                role="button"
                                tabIndex={0}
                                aria-label={`${p.title} — open project details`}
                                onClick={() => setSelected(p)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelected(p);
                                    }
                                }}
                                className="group relative block cursor-pointer overflow-hidden bg-white text-left shadow-lift"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    {p.before ? (
                                        <BeforeAfterSlider
                                            before={p.before}
                                            after={p.img}
                                            alt={p.title}
                                            className="h-full w-full"
                                        />
                                    ) : (
                                        <>
                                            <img
                                                src={p.img}
                                                alt={p.title}
                                                loading="lazy"
                                                className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-espresso/0 transition-colors duration-500 group-hover:bg-espresso/15" />
                                            <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center bg-bone/90 text-espresso opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:opacity-100">
                                                <Expand className="h-4 w-4" />
                                            </span>
                                        </>
                                    )}
                                </div>
                                <div className="p-5">
                                    <h3 className="font-serif text-xl text-espresso">{p.title}</h3>
                                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-fog">
                                        {p.scope} — {p.stage}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            <AnimatePresence>
                {selected && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelected(null)}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-espresso/80 p-4 backdrop-blur-sm sm:p-8"
                    >
                        <motion.figure
                            initial={{ opacity: 0, y: 32, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 32, scale: 0.97 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative max-h-full w-full max-w-4xl overflow-hidden bg-bone shadow-frame"
                        >
                            {selected.before ? (
                                <BeforeAfterSlider
                                    before={selected.before}
                                    after={selected.img}
                                    alt={selected.title}
                                    className="aspect-[4/3] max-h-[70vh] w-full"
                                />
                            ) : (
                                <img
                                    src={selected.img}
                                    alt={selected.title}
                                    className="max-h-[70vh] w-full object-cover"
                                />
                            )}
                            <figcaption className="flex flex-col gap-1 p-6 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <h3 className="font-serif text-2xl text-espresso">{selected.title}</h3>
                                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-fog">
                                        {selected.scope} — {selected.stage}
                                    </p>
                                </div>
                                <button
                                    data-testid="portfolio-lightbox-close"
                                    onClick={() => setSelected(null)}
                                    aria-label="Close preview"
                                    className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center bg-bone/90 text-espresso transition-colors hover:bg-oak hover:text-bone"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
